<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useId, useSlots, type HTMLAttributes } from 'vue';
import { useI18n } from 'vue-i18n';

import { useCollapse } from '@sg-core/src/View/Components/Atoms/Collapse';
import '@sg-core/src/View/Components/Atoms/Collapse/collapse.css';

import { PhxsgButton } from '../../Atoms/Button';
import { PhxsgHeading } from '../../Atoms/Heading';
import { PhxsgText } from '../../Atoms/Text';

import { CLAMPED_CLASS, isCollapseAnimating, isContentOverflowing } from './overflow';

export type PhxsgTextCardTitleTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface PhxsgTextCardProps {
  title?: string;
  titleUse?: PhxsgTextCardTitleTag;
  titleUppercase?: boolean;
  titleClass?: HTMLAttributes['class'];
  subtitle?: string;
  content?: string;
  expandable?: boolean;
  lineClamp?: number;
  readMoreButton?: boolean;
  expandLabel?: string;
  closeLabel?: string;
}

const {
  title,
  titleUse = 'h3',
  titleUppercase = false,
  titleClass,
  subtitle,
  content,
  expandable = false,
  lineClamp = 2,
  readMoreButton = false,
  expandLabel,
  closeLabel,
} = defineProps<PhxsgTextCardProps>();

const slots = useSlots();
const { t } = useI18n();
const contentId = useId();

const contentRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const hasOverflow = ref(false);

const hasSubtitle = computed(() => !!subtitle || !!slots.subtitle);
const hasContent = computed(() => !!content || !!slots.content);
const hasActions = computed(() => !!slots.actions);
const toggleLabel = computed(() =>
  isOpen.value ? (closeLabel ?? t('Button.readLess')) : (expandLabel ?? t('Button.readMore')),
);

const contentStyles = computed(() => (expandable ? { '--text-card-line-clamp': lineClamp } : {}));

useCollapse(contentRef, isOpen, {
  minHeight: () => (expandable ? `calc(${lineClamp} * 1lh)` : undefined),
  observeResize: false,
});

let observer: ResizeObserver | undefined;

const toggle = (): void => {
  isOpen.value = !isOpen.value;
};

onMounted(() => {
  if (!contentRef.value) {
    return;
  }

  observer = new ResizeObserver(() => {
    const element = contentRef.value;

    if (!element || !expandable || isCollapseAnimating(element)) {
      return;
    }

    hasOverflow.value = isContentOverflowing(element, lineClamp);
  });

  observer.observe(contentRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <div class="phxsg-text-card">
    <div v-if="title" class="phxsg-text-card__heading">
      <PhxsgHeading :use="titleUse" :uppercase="titleUppercase" :class="['phxsg-text-card__title', titleClass]">
        {{ title }}
      </PhxsgHeading>

      <PhxsgText v-if="hasSubtitle" use="span" variant="label" size="medium" uppercase color="secondary">
        <slot name="subtitle">{{ subtitle }}</slot>
      </PhxsgText>
    </div>

    <div
      v-if="hasContent"
      :id="contentId"
      ref="contentRef"
      :class="[
        'phxsg-text-card__content',
        {
          'phxsg-collapse phxsg-force-mount': expandable,
          [CLAMPED_CLASS]: expandable && !isOpen,
        },
      ]"
      :style="contentStyles"
    >
      <slot name="content">{{ content }}</slot>

      <button
        v-if="expandable && !readMoreButton && hasOverflow"
        type="button"
        class="phxsg-text-card__read-more-button"
        :aria-expanded="isOpen"
        :aria-controls="contentId"
        @click="toggle"
      >
        {{ toggleLabel }}
      </button>
    </div>

    <div v-if="expandable && readMoreButton && hasOverflow" class="phxsg-text-card__actions">
      <PhxsgButton variant="fade" :aria-expanded="isOpen" :aria-controls="contentId" @click="toggle">
        {{ toggleLabel }}
      </PhxsgButton>
    </div>

    <div v-if="hasActions" class="phxsg-text-card__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style src="./text-card.css" />
